import type { PartyPositionsRecord } from "odyssey-shared";
import type { PartyMember } from "./members.ts";
import { placeOf } from "./rows.ts";

/**
 * Where the party stands, as the page holds it (ADR-100 decisions 10 to 16): the last `partyPositions` frame by roster
 * place in tiles, and the join of that with the roster that names who is on this map, who is on another and what they
 * are called. A frame is the whole set of members on the player's map, so a frame of none clears every mark; the
 * session's end clears them too. Nothing here is drawn: the Map, the minimap and the compass read it.
 */

/**
 * One member's position as the last frame held it: the roster place and the ground point in tiles.
 */
export interface HeldPosition {
  readonly place: number;
  readonly x: number;
  readonly y: number;
}

/**
 * A member who stands on the player's map: roster place, first name and ground point in tiles.
 */
export interface PartyHere {
  readonly place: number;
  readonly name: string;
  readonly x: number;
  readonly y: number;
}

/**
 * A member who is online on another map: roster place and first name, with no position.
 */
export interface PartyAway {
  readonly place: number;
  readonly name: string;
}

/**
 * The party as the map surfaces draw it.
 */
export interface PartyOnMap {
  readonly here: readonly PartyHere[];
  readonly away: readonly PartyAway[];
}

/**
 * The positions of a party that is not on the map.
 */
const NONE: readonly HeldPosition[] = [];

/**
 * The view of a party that is not on the map, one object for every such view.
 */
export const NO_PARTY: PartyOnMap = { here: [], away: [] };

/**
 * Sixteenths in a tile.
 */
const SIXTEENTHS = 16;

/**
 * Whether two lists hold the same positions in the same order.
 *
 * @param a One list.
 * @param b The other.
 * @returns True when every entry matches.
 */
function samePositions(a: readonly HeldPosition[], b: readonly HeldPosition[]): boolean {
  return a.length === b.length && a.every((entry, index) => entry.place === (b[index] as HeldPosition).place && entry.x === (b[index] as HeldPosition).x && entry.y === (b[index] as HeldPosition).y);
}

/**
 * The last `partyPositions` frame, by roster place, in tiles. It tells its listeners only when the set differs from the
 * one held, so a frame that repeats the last, which is every frame of a party standing still, costs nothing downstream.
 *
 * @example
 * const book = new PartyPositionsBook();
 * book.subscribe(() => minimap.setParty(partyOnMap(book.held(), roster, self).here));
 * book.receive(record);
 */
export class PartyPositionsBook {
  private positions: readonly HeldPosition[] = NONE;
  private readonly listeners = new Set<() => void>();

  /**
   * Takes a decoded frame, which replaces the set held.
   *
   * @param record The frame; only its first `count` entries are read.
   * @returns True when the set now held differs from the one before.
   */
  public receive(record: Readonly<PartyPositionsRecord>): boolean {
    if (record.count === 0) {
      return this.replace(NONE);
    }
    const held = this.positions;
    let same = held.length === record.count;
    for (let index = 0; same && index < record.count; index += 1) {
      const entry = held[index] as HeldPosition;
      same = entry.place === record.places[index] && entry.x * SIXTEENTHS === record.xs[index] && entry.y * SIXTEENTHS === record.ys[index];
    }
    if (same) {
      return false;
    }
    const next: HeldPosition[] = [];
    for (let index = 0; index < record.count; index += 1) {
      next.push({ place: record.places[index] as number, x: (record.xs[index] as number) / SIXTEENTHS, y: (record.ys[index] as number) / SIXTEENTHS });
    }
    return this.replace(next);
  }

  /**
   * Forgets every position, as a change of map, a reconnect and the end of a session do.
   */
  public reset(): void {
    this.replace(NONE);
  }

  /**
   * The positions now.
   *
   * @returns The set held, the same array until a frame changes it.
   */
  public held(): readonly HeldPosition[] {
    return this.positions;
  }

  /**
   * Listens for changes.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Holds a set when it differs from the one held.
   *
   * @param next The set.
   * @returns True when it was held and the listeners told.
   */
  private replace(next: readonly HeldPosition[]): boolean {
    if (samePositions(this.positions, next)) {
      return false;
    }
    this.positions = next;
    for (const listener of [...this.listeners]) {
      listener();
    }
    return true;
  }
}

/**
 * Joins the positions to the roster: each member the frame names whom the roster seats on the player's map, with their
 * first name, in the frame's order, and each member online on another map. The player's own seat, a seat the roster
 * does not hold, an offline seat and a member the roster puts on another map (whatever a frame not yet replaced still
 * says) have no mark.
 *
 * @param held The positions the book holds.
 * @param roster The party's roster, whole.
 * @param self The player's own seat, or null.
 * @returns The members on the map and the members away.
 */
export function partyOnMap(held: readonly HeldPosition[], roster: readonly PartyMember[], self: PartyMember | null): PartyOnMap {
  const here: PartyHere[] = [];
  for (const position of held) {
    const seat = roster.find((member) => member.place === position.place);
    if (seat === undefined || (self !== null && seat.place === self.place) || placeOf(seat, self) !== "here") {
      continue;
    }
    here.push({ place: seat.place, name: seat.firstName, x: position.x, y: position.y });
  }
  const away: PartyAway[] = roster.filter((member) => (self === null || member.place !== self.place) && placeOf(member, self) === "elsewhere").map((member) => ({ place: member.place, name: member.firstName }));
  return here.length === 0 && away.length === 0 ? NO_PARTY : { here, away };
}

/**
 * The join of `partyOnMap` kept by the page that owns it: the view is the same object while the positions, the roster
 * and the player's seat are the same objects, so what is told of it can compare by identity.
 */
export class PartyOnMapMemo {
  private held: readonly HeldPosition[] | null = null;
  private roster: readonly PartyMember[] | null = null;
  private self: PartyMember | null = null;
  private view: PartyOnMap = NO_PARTY;

  /**
   * Joins the positions to the roster, or returns the last join when its inputs are unchanged.
   *
   * @param held The positions the book holds.
   * @param roster The party's roster, whole.
   * @param self The player's own seat, or null.
   * @returns The members on the map and the members away.
   */
  public join(held: readonly HeldPosition[], roster: readonly PartyMember[], self: PartyMember | null): PartyOnMap {
    if (this.held !== held || this.roster !== roster || this.self !== self) {
      this.held = held;
      this.roster = roster;
      this.self = self;
      this.view = partyOnMap(held, roster, self);
    }
    return this.view;
  }
}
